// PURPOSE: Shared human-readable outreach state and Toronto cadence labels.
// CONTRACT: Spec 065 stages and email rungs; date-only values retain their calendar day.
// DEPENDS: Native record data and Intl date formatting.
// CEILING: Presentation only; does not infer permissions or advance the cadence.
import type { NativeRecord } from './types';

const NUMBERS: Record<string, number> = { first_touch: 1, mark1: 2, mark2: 3 };
const dayLabel = (value: string) => {
  const date = new Date(`${value.slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'America/Toronto' }).format(date).replace(',', '');
};

/** Spec 088: the latest drafted email has not been reported sent. */
export function awaitingSend(record: NativeRecord) {
  const rung = String(record.data.last_drafted_rung || '');
  if (!NUMBERS[rung]) return false;
  const copies = record.data.cadence_emails as Record<string, { sent_on?: string }> | undefined;
  return !copies?.[rung]?.sent_on && record.data.last_sent_rung !== rung;
}

/** Spec 088 status line: what was actually sent, and only then what is due. */
export function sendStatus(record: NativeRecord) {
  if (awaitingSend(record)) return `Email ${NUMBERS[String(record.data.last_drafted_rung)]} drafted · not sent yet`;
  const sentRung = String(record.data.last_sent_rung || '');
  const sent = NUMBERS[sentRung];
  const copies = record.data.cadence_emails as Record<string, { sent_on?: string }> | undefined;
  const sentOn = dayLabel(String(copies?.[sentRung]?.sent_on || record.data.last_sent_on || ''));
  const sentText = sent ? `Email ${sent} sent${sentOn ? ` ${sentOn}` : ''}` : '';
  if (sent === 3) return 'All 3 emails sent · call next';
  const next = NUMBERS[String(record.data.next_touch_rung || '')];
  const due = dayLabel(String(record.data.next_touch_due || ''));
  if (next && due) return [sentText, `Email ${next} due ${due}`].filter(Boolean).join(' · ');
  if (next && record.data.draft_requested_at && next === 1) return 'Email 1 · draft requested';
  return sentText || (next ? `Email ${next} · not scheduled` : '');
}

export const outreachStates: Record<string, string> = {
  screening: 'Screening', accepted: 'Claimed', in_cadence: 'Draft requested',
  replied: 'Replied', archived: 'Archived',
};

export function outreachStateLabel(value: unknown, record?: NativeRecord) {
  if (value === 'in_cadence' && record && (record.data.draft_requested_at || record.data.last_drafted_rung)) return cadenceLabel(record, true);
  return outreachStates[String(value)] || 'Outreach';
}

export function draftLabel(record: NativeRecord) {
  const rung = String(record.data.last_drafted_rung || '');
  const number = ({ first_touch: 1, mark1: 2, mark2: 3 } as Record<string, number>)[rung];
  if (!number) return record.data.draft_requested_at ? 'Draft requested' : '';
  const day = String(record.data.last_drafted_on || '');
  const date = new Date(`${day.slice(0, 10)}T12:00:00Z`);
  const label = Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'America/Toronto' }).format(date).replace(',', '');
  return `Drafted · email ${number}${label ? ` · ${label}` : ''}`;
}

export function cadenceLabel(record: NativeRecord, summary = false) {
  if (record.data.outreach_state !== 'in_cadence') return '';
  if (!summary && (awaitingSend(record) || record.data.last_sent_rung)) return sendStatus(record);
  const numbers: Record<string, number> = { first_touch: 1, mark1: 2, mark2: 3 };
  const drafted = numbers[String(record.data.last_drafted_rung || '')];
  const sentRung = String(record.data.last_sent_rung || '');
  const copies = record.data.cadence_emails as Record<string, { sent_on?: string }> | undefined;
  const sentOn = sentRung && sentRung === record.data.last_drafted_rung ? copies?.[sentRung]?.sent_on : undefined;
  const latestOn = sentOn || record.data.last_drafted_on;
  if (drafted && latestOn) {
    const date = new Date(String(latestOn).slice(0, 10) + 'T12:00:00Z');
    if (!Number.isNaN(date.getTime())) {
      const label = `Email ${drafted} · ${sentOn ? 'Sent' : 'Drafted'} ${new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'America/Toronto' }).format(date)}`;
      const next = numbers[String(record.data.next_touch_rung || '')];
      const due = String(record.data.next_touch_due || '');
      const dueDate = new Date(due.length === 10 ? `${due}T12:00:00Z` : due);
      return !summary && next && !Number.isNaN(dueDate.getTime())
        ? `${label} · Email ${next} due ${new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'America/Toronto' }).format(dueDate).replace(',', '')}`
        : label;
    }
  }
  const rung = String(record.data.next_touch_rung || '');
  const number = ({ first_touch: 1, mark1: 2, mark2: 3 } as Record<string, number>)[rung];
  if (!number) return summary ? 'Calling next' : 'All 3 emails drafted · call next';
  if (record.data.draft_requested_at) return `Email ${number} · Draft requested`;
  const due = String(record.data.next_touch_due || '');
  const date = new Date(due.length === 10 ? `${due}T12:00:00Z` : due);
  if (Number.isNaN(date.getTime())) return `Email ${number} · No due date`;
  const parts = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'America/Toronto',
  }).formatToParts(date);
  const part = (type: string) => parts.find(item => item.type === type)?.value;
  return `Email ${number}${summary ? ' · ' : ' due '}${part('weekday')} ${part('day')} ${part('month')}`;
}

/** Next email due, on its own: "Email 2 due Mon 19 Oct". Spec 066 Follow-ups rows lead with this. */
export function nextDueLabel(record: NativeRecord) {
  if (awaitingSend(record) || record.data.last_sent_rung) return sendStatus(record);
  const number = ({ first_touch: 1, mark1: 2, mark2: 3 } as Record<string, number>)[String(record.data.next_touch_rung || '')];
  if (!number) return 'All 3 emails drafted · call next';
  const due = String(record.data.next_touch_due || '');
  const date = new Date(due.length === 10 ? `${due}T12:00:00Z` : due);
  if (Number.isNaN(date.getTime())) return `Email ${number} · No due date`;
  return `Email ${number} due ${new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'America/Toronto' }).format(date).replace(',', '')}`;
}
